import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Select from 'antd/lib/select';
import Alert from 'antd/lib/alert';
import { TRY_CREATE_ISSUE, tryCreateIssue, TriageSortBy, TriageStatusGroup } from '@/actions/triage';
import { IssueSourceType } from '@/types';
import { RootState } from '@/reducers/reducers';
import { selectAuthCredentials, selectRelease, selectReleasesForAppId } from '@/selectors/selectors';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { getBuildsForVersion, BuildSummary } from '@/api';
import { IssueCandidatesSelect } from './IssueCandidatesSelect';

interface CreateIssueModalProps {
    open: boolean;
    onClose: () => void;
    orgId: string;
    appId: string;
    // Set at the release-level triage route: the release selector is then locked + prefilled to this release.
    lockedReleaseId?: string;
    statusGroup: TriageStatusGroup;
    sources: IssueSourceType[];
    sortBy: TriageSortBy;
}

interface CreateIssueFormValues {
    title: string;
    description?: string;
    version?: string;
    build?: string;
}

export const CreateIssueModal: React.FC<CreateIssueModalProps> = ({
    open,
    onClose,
    orgId,
    appId,
    lockedReleaseId,
    statusGroup,
    sources,
    sortBy,
}) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm<CreateIssueFormValues>();
    const [sourceIssueIds, setSourceIssueIds] = React.useState<string[]>([]);

    const releases = useSelector((state: RootState) => selectReleasesForAppId(state, appId));
    const lockedRelease = useSelector((state: RootState) =>
        lockedReleaseId ? selectRelease(state, lockedReleaseId) : undefined,
    );
    const lockedVersion = lockedRelease?.version;

    // Build cascades from the selected release, so it's only enabled once a release/version is set.
    const selectedVersion = Form.useWatch('version', form) ?? lockedVersion;

    const auth = useSelector((state: RootState) => selectAuthCredentials(state));
    const [builds, setBuilds] = React.useState<BuildSummary[]>([]);
    const [buildsLoading, setBuildsLoading] = React.useState(false);

    React.useEffect(() => {
        let cancelled = false;
        if (selectedVersion) {
            setBuildsLoading(true);
            getBuildsForVersion(orgId, appId, selectedVersion, auth)
                .then((res) => !cancelled && setBuilds(res.builds ?? []))
                .catch(() => !cancelled && setBuilds([]))
                .finally(() => !cancelled && setBuildsLoading(false));
        } else {
            setBuilds([]);
        }
        return () => {
            cancelled = true;
        };
    }, [selectedVersion, orgId, appId, auth]);

    const isSaving = useIsLoading(TRY_CREATE_ISSUE, appId);
    const saveError = useError(TRY_CREATE_ISSUE, appId);
    const saveSucceeded = useUpdated(TRY_CREATE_ISSUE, appId);

    // Close + reset once the create succeeds.
    React.useEffect(() => {
        if (saveSucceeded) {
            form.resetFields();
            setSourceIssueIds([]);
            onClose();
        }
    }, [saveSucceeded]);

    const onSubmit = (values: CreateIssueFormValues) => {
        dispatch(
            tryCreateIssue({
                orgId,
                appId,
                title: values.title.trim(),
                description: values.description?.trim() ?? '',
                version: values.version?.trim() || undefined,
                build: values.build?.trim() || undefined,
                sourceIssueIds: sourceIssueIds.length > 0 ? sourceIssueIds : undefined,
                statusGroup,
                sources,
                sortBy,
            }),
        );
    };

    return (
        <Modal
            title="Add a new issue"
            open={open}
            onCancel={onClose}
            onOk={() => form.submit()}
            okText="Create issue"
            confirmLoading={isSaving}
            destroyOnClose
            width={560}
        >
            <Form form={form} layout="vertical" onFinish={onSubmit} requiredMark preserve={false}>
                {saveError && <Alert type="error" message={saveError} showIcon style={{ marginBottom: 16 }} />}
                <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
                    <Input placeholder="Title of the issue" autoFocus />
                </Form.Item>
                <Form.Item name="description" label="Description">
                    <Input.TextArea placeholder="Describe the issue" autoSize={{ minRows: 3, maxRows: 8 }} />
                </Form.Item>
                <Form.Item name="version" label={lockedReleaseId ? 'Version' : 'Version'} initialValue={lockedVersion}>
                    <Select
                        placeholder="Select a version"
                        disabled={!!lockedReleaseId}
                        allowClear={!lockedReleaseId}
                        showSearch
                        optionFilterProp="label"
                        onChange={() => form.setFieldsValue({ build: undefined })}
                        options={releases.map((release) => ({
                            value: release.version,
                            label: release.displayableVersion || release.version,
                        }))}
                    />
                </Form.Item>
                <Form.Item name="build" label="Build">
                    <Select
                        placeholder={selectedVersion ? 'Select a build' : 'Select a version first'}
                        disabled={!selectedVersion}
                        loading={buildsLoading}
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        notFoundContent={buildsLoading ? 'Loading…' : 'No builds found'}
                        options={builds.map((b) => ({ value: b.identifier, label: b.identifier }))}
                    />
                </Form.Item>
                <Form.Item label="Attach existing sources">
                    <IssueCandidatesSelect
                        orgId={orgId}
                        appId={appId}
                        open={open}
                        value={sourceIssueIds}
                        onChange={setSourceIssueIds}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};
